import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { CategoryRow } from '../../categoriesMeta';

type DeleteCategoryDialogProps = {
    /** The category being removed, or `null` when closed. */
    category: CategoryRow | null;
    isPending: boolean;
    /** The server's refusal, in the server's own words. */
    errorMessage: string | null;
    /**
     * How many operations are blocking, when the refusal carried a count.
     * `null` for a refusal that is not about a count.
     */
    blockingCount: number | null;
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Confirmation for removing a category.
 *
 * **Worded as what it is: a removal, and it takes nothing with it.** Nothing
 * that was filed under this category can still be filed under it — the server
 * refuses while any operation is — so no entry changes, no recipe changes, and
 * no history is rewritten. Saying so is what keeps this dialog from reading like
 * the block/unblock action next door, which is about what an operation may do.
 *
 * **The refusal keeps the dialog open**, and is where the reason belongs: it
 * names the operations standing in the way, and those are what the user has to
 * go and refile. Closing on a refusal would leave them pressing the same button
 * to find out why nothing happened. The message is the server's own prose,
 * untranslated, because it carries names this client does not hold.
 */
export const DeleteCategoryDialog: React.FC<DeleteCategoryDialogProps> = ({
    category,
    isPending,
    errorMessage,
    blockingCount,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal
            isOpen={category !== null}
            onOpenChange={(open) => !open && onCancel()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-md"
                        {...getNodeTestIdProp('operation-categories-delete')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t('operations.categories.remove.title', {
                                    name: category?.label ?? '',
                                })}
                            </Modal.Heading>
                            <p className="text-sm text-muted">
                                {t('operations.categories.remove.subtitle')}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-3 pb-2">
                            <p className="flex items-start gap-2 text-xs text-muted">
                                <Icon
                                    aria-hidden
                                    className="mt-0.5 shrink-0"
                                    icon="gravity-ui:circle-info"
                                />
                                {t('operations.categories.remove.note')}
                            </p>
                            {errorMessage ? (
                                <div
                                    className="flex flex-col gap-1 rounded-xl bg-surface-secondary p-4"
                                    {...getNodeTestIdProp(
                                        'operation-categories-delete-error',
                                    )}
                                >
                                    {blockingCount === null ? null : (
                                        <span className="text-sm font-medium text-foreground">
                                            {t(
                                                'operations.categories.remove.blockedCount',
                                                { count: blockingCount },
                                            )}
                                        </span>
                                    )}
                                    <span className="text-sm text-danger">
                                        {errorMessage}
                                    </span>
                                </div>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                isDisabled={isPending}
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'operation-categories-delete-cancel',
                                )}
                            >
                                {t('operations.categories.actions.cancel')}
                            </Button>
                            <Button
                                isPending={isPending}
                                onPress={onConfirm}
                                variant="danger-soft"
                                {...getNodeTestIdProp(
                                    'operation-categories-delete-confirm',
                                )}
                            >
                                {t('operations.categories.actions.remove')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
